<template>
  <div class="knowledge-article-editor-container">
    <h1 class="editor-title">Article Editor</h1>
    <!-- Article Meta: Title and Category -->
    <div class="article-meta">
      <input type="text" v-model="title" placeholder="Enter article title" class="title-input" />
      <input type="text" v-model="category" placeholder="Enter article category" class="category-input" />
    </div>
    <!-- EditorJS Container -->
    <div id="editorjs"></div>
    <button @click="getEditorDataAndSave" class="save-article-btn">Save Article</button>
    <div v-if="successMessage" class="success-message">{{ successMessage }}</div>
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script>
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import KnowledgeArticleEditorLogic from "./KnowledgeArticleEditorLogic.js";

export default {
  name: "KnowledgeArticleEditor",
  mixins: [KnowledgeArticleEditorLogic],
  props: {
    // The articleId comes from the route as /knowledge-editor/{articleId}
    initialArticleId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      editor: null
    };
  },
  async mounted() {
    try {
      // Initialize EditorJS with Header and Paragraph tools.
      // Registering Paragraph ensures that if your saved JSON contains paragraph blocks, they are properly processed.
      this.editor = new EditorJS({
        holder: 'editorjs',
        placeholder: 'Write your help centre article here...',
        tools: {
          header: {
            class: Header,
            inlineToolbar: ['marker', 'bold', 'italic', 'underline', 'link'],
            config: {
              placeholder: 'Enter a header'
            },
            sanitize: {
              level: true,
              text: true
            },
            shortcut: 'CMD+SHIFT+H'
          },
          paragraph: {
            class: Paragraph,
            inlineToolbar: true,
            // The paragraph tool has a default sanitize getter, but you can define one explicitly if needed.
            sanitize: {
              text: true
            }
          }
        },
        // If there is an existing article, load its content (stored as JSON)
        data: this.description ? JSON.parse(this.description) : {}
      });
    } catch (e) {
      console.error("Error initializing EditorJS:", e);
      this.error = "Error initializing editor.";
    }
  },
  watch: {
    description(newVal) {
      if (this.editor && newVal) {
        this.$nextTick(async () => {
          try {
            const data = JSON.parse(newVal);
            if (data.blocks && data.blocks.length > 0) {
              try {
                await this.editor.clear();
              } catch (err) {
                if (!err.message.includes("Can't find a Block to remove")) {
                  throw err;
                }
              }
            }
            await this.editor.render(data);
          } catch (e) {
            console.error("Error updating EditorJS content:", e);
          }
        });
      }
    }
  },
  methods: {
    async getEditorDataAndSave() {
      try {
        const outputData = await this.editor.save();
        // Store the raw EditorJS JSON string in description
        this.description = JSON.stringify(outputData);
        await this.saveArticle();
      } catch (err) {
        console.error("Error saving editor data", err);
        this.error = "Error saving article content.";
      }
    }
  }
};
</script>

<style scoped>
.knowledge-article-editor-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  border-radius: 12px;
  font-family: "Poppins", sans-serif;
  color: #111827;
}

.editor-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
}

.article-meta {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.title-input,
.category-input {
  flex: 1;
  font-family: "Courier Prime", monospace;
  font-weight: 400;
  font-style: normal;
  width: 92%;
  padding: 12px;
  border: 1px solid #C9BF9B;
  box-shadow: inset 0px 3px 2px #dacb9754;
  border-radius: 9px;
  font-size: 14px;
  color: #111827;
  background-color: #fff;
}

#editorjs {
  min-height: 300px;
  background-color: #fff;
  border: 1px solid #707070;
  border-radius: 15px;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #C9BF9B;
  box-shadow: 0px 4px 4px #c9bf9b54;
}

.save-article-btn {
  padding: 10px;
  font-size: 16px;
  font-weight: bold;
  color: #000;
  background-color: #EA845A;
  border: 1px solid #C06C4A;
  box-shadow: inset 0px 2px 3px 0px #FFB681, 0px 2px 3px 0px #FFB681;
  border-radius: 15px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: block;
  margin: 0 auto;
}

.save-article-btn:hover {
  background-color: rgb(255, 141, 92);
}

.success-message {
  color: #155724;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 1rem;
  text-align: center;
  margin-top: 1rem;
}

.error-message {
  color: #dc2626;
  font-size: 1rem;
  text-align: center;
  margin-top: 1rem;
}
</style>
